import UserStore from '../../stores/UserStore';
import template from '../../helpers/template';
import typeOf from '../../helpers/typeOf';
import { getTranslation } from '../../helpers/translationHelper';
import store from '../../stores/redux';
import { metrics, treeMetric } from './config';

let currentKeyGroupBy = 'Day';
let defBinaryMetric = '';
const isBoolean = (value) => typeof value === 'boolean';

const resolveIsSortable = (metricConfig = {}, defaultValue = false) => {
    if (isBoolean(metricConfig?.isSortable)) {
        return metricConfig.isSortable;
    }
    return defaultValue;
};

const getTitle = (key, titleTemplate) => {
    const t = getTranslation('report', {});
    let title = t.title?.[key] ?? key;
    if (titleTemplate) title = template(titleTemplate, { title });
    return title;
};

const getDescription = (key) => {
    const t = getTranslation('report', {});
    return t.description?.[key] ?? '';
};

const setKeyGroupBy = (key) => {
    if (key) currentKeyGroupBy = key;
};

const getGroupBy = (data = {}) => {
    const metricGroupBy = metrics.GroupBy?.[currentKeyGroupBy] || {};
    const groupBySortable = resolveIsSortable(metricGroupBy, resolveIsSortable(metrics.GroupBy, false));
    return {
        ...data,
        title: metricGroupBy.title || currentKeyGroupBy,
        isSortable: groupBySortable
    };
};

const find = (fn, data, params = {}) => {
    const level = params.level || 0;
    const nextLevel = level + 1;
    let result = null;

    (data[level] || [])
        .filter((item) => !params.parentKey || item.config.parentKey === params.parentKey)
        .find((item) => {
            const r = fn(item, { level, nextLevel });
            result = r;
            return params.find && !!r;
        });

    return result;
};

const findMetrics = (list, config) => {
    for (const key of config.metrics || []) {
        const m = metrics[key] || {};
        list.push({
            title: getTitle(m.titleKey || key),
            description: getDescription(m.titleKey || key),
            isSortable: resolveIsSortable(m, false),
            config: { key, parentKey: config.key },
        });
    }
};

const getTitleChain = (key, delimiter = ' → ') => {
    const titles = [];
    const fn = (item, params) => {
        const { config } = item;
        if (config.metrics && config.metrics.includes(key)) {
            titles.unshift(getTitle(metrics[key].titleKey || key));
            titles.unshift(getTitle(config.titleKey || config.key));
            return true;
        }
        if (config.children) {
            const found = find(fn, treeMetric, { find: true, level: params.nextLevel, parentKey: config.key });
            if (found) titles.unshift(getTitle(config.titleKey || config.key));
            return found;
        }
        return false;
    };

    if (key === 'GroupBy') {
        titles.push(getGroupBy().title);
    } else {
        find(fn, treeMetric, { find: true });
    }

    return titles.join(delimiter);
};

const getMultiLevelHeaderNames = () => {
    const header = Array.from({ length: treeMetric.length + 1 }, () => []);
    let countMetrics = 0;

    const fn = (item, params) => {
        const { config } = item;
        let count = 0;
        let rowItem = {};

        if (config.key !== 'GroupBy') {
            if (config.metrics) {
                count += config.metrics.length;
                findMetrics(header[treeMetric.length], config);
            } else {
                count = find(fn, treeMetric, { level: params.nextLevel, parentKey: config.key }) || 0;
            }

            rowItem = {
                ...item,
                title: getTitle(config.titleKey || config.key, config.titleTemplate),
                description: getDescription(config.key),
                colspan: count,
            };
        } else {
            rowItem = getGroupBy(item);
        }

        header[params.level].push(rowItem);
        countMetrics += count;
        return count;
    };

    find(fn, treeMetric);
    return header;
};

const getDefStrMetric = () => {
    const s = store.getState?.() || {};
    const def = s.serverStorage?.store?.report?.metrics;
    return def || getStrMetric();
};

const getStrMetric = (list) => {
    const isArray = list && typeOf.isArray(list);
    if (!isArray && defBinaryMetric) return defBinaryMetric;

    let str = '';
    const keys = Object.keys(metrics);
    for (const key of keys) {
        if (key === 'GroupBy') {
            str += '1';
            continue;
        }
        if (isArray) {
            str += list.includes(key) ? '1' : '0';
        } else {
            str += metrics[key].visible ? '1' : '0';
        }
    }

    if (!isArray) defBinaryMetric = str;
    return str;
};

const getKeysMetric = (param) => {
    const keys = [];
    const all = Object.keys(metrics);
    all.forEach((key, i) => {
        if (typeOf.isString(param)) {
            if (+param.charAt(i)) keys.push(key);
        } else if (typeOf.isBoolean(param) && param) {
            if (metrics[key].visible) keys.push(key);
        } else {
            keys.push(key);
        }
    });
    return keys;
};

const updateVisible = (data, keys) => {
    let total = 0;
    const length = data.length - 1;

    const fn = (item, params) => {
        const { config } = item;
        let count = 0;

        if (params.level === 0) total = 0;

        if (config.metrics) {
            for (const metricKey of config.metrics) {
                const leaf = (data[length] || []).find((x) => x.config.key === metricKey);
                const visible = keys.includes(metricKey);
                if (leaf) leaf.visible = visible;
                if (visible) count += 1;
            }
        } else if (config.children) {
            count = find(fn, data, { level: params.nextLevel, parentKey: config.key }) || 0;
        }

        item.visible = config.key === 'GroupBy' ? true : !!count;
        item.colspan = count;
        total += count;
        return count;
    };

    find(fn, data);
};

const Utils = {
    getTitleChain,
    getTitle,
    getDescription,
    setKeyGroupBy,
    getGroupBy,
    findMetrics,
    find,
    getMultiLevelHeaderNames,
    getDefStrMetric,
    getStrMetric,
    getKeysMetric,
    updateVisible
};

export default Utils;
